/**
 * WooCommerce Cart API response.
 * @see {@link https://github.com/woocommerce/woocommerce/blob/trunk/plugins/woocommerce/src/StoreApi/docs/cart.md#cart-response}
 */

import type { ShippingMethodId } from "@/constants/enums";
import type { BillingAddress, ShippingAddress } from "@/types/common";

export interface Cart {
  items: CartItem[];
  coupons: Coupon[];
  fees: any[]; // 沒有提供 fees 結構，暫時用 any[]
  totals: CartTotals;
  shipping_address: ShippingAddress;
  billing_address: BillingAddress;
  payment_methods: string[];
  needs_payment: boolean;
  needs_shipping: boolean;
  items_count: number;
  items_weight: number;
  payment_requirements: string[];
  errors: CartError[];
  has_calculated_shipping: boolean;
  shipping_rates: ShippingRate[];
}

export interface CartItem {
  key: string;
  id: number;
  type: CartItemType;
  quantity: number;
  quantity_limits: QuantityLimits;
  name: string;
  short_description: string;
  description: string;
  sku: string;
  low_stock_remaining: number | null;
  backorders_allowed: boolean;
  show_backorder_badge: boolean;
  sold_individually: boolean;
  permalink: string;
  images: ProductImage[];
  variation: Variation[];
  item_data: any[];
  prices: Prices;
  totals: ItemTotals;
  catalog_visibility: string;
  extensions: Record<string, unknown>;
}

export enum CartItemType {
  Simple = "simple",
  Variable = "variation",
}

export interface Variation {
  attribute: string;
  value: string;
}

export interface QuantityLimits {
  minimum: number;
  maximum: number;
  multiple_of: number;
  editable: boolean;
}

export interface ProductImage {
  id: number;
  src: string;
  thumbnail: string;
  srcset: string;
  sizes: string;
  name: string;
  alt: string;
}

export interface PriceRange {
  max_amount: string;
  min_amount: string;
}

export interface Prices {
  price: string;
  regular_price: string;
  sale_price: string;
  price_range: PriceRange | null;
  currency_code: string;
  currency_symbol: string;
  currency_minor_unit: number;
  currency_decimal_separator: string;
  currency_thousand_separator: string;
  currency_prefix: string;
  currency_suffix: string;
  raw_prices: RawPrices;
}

export interface RawPrices {
  precision: number;
  price: string;
  regular_price: string;
  sale_price: string;
}

export interface ItemTotals {
  line_subtotal: string;
  line_subtotal_tax: string;
  line_total: string;
  line_total_tax: string;
  currency_code: string;
  currency_symbol: string;
  currency_minor_unit: number;
  currency_decimal_separator: string;
  currency_thousand_separator: string;
  currency_prefix: string;
  currency_suffix: string;
}

export interface Coupon {
  code: string;
  discount_type: string;
  totals: CouponTotals;
}

export interface CouponTotals {
  total_discount: string;
  total_discount_tax: string;
  currency_code: string;
  currency_symbol: string;
  currency_minor_unit: number;
  currency_decimal_separator: string;
  currency_thousand_separator: string;
  currency_prefix: string;
  currency_suffix: string;
}

export interface CartTotals {
  total_items: string;
  total_items_tax: string;
  total_fees: string;
  total_fees_tax: string;
  total_discount: string;
  total_discount_tax: string;
  total_shipping: string | null;
  total_shipping_tax: string | null;
  total_price: string;
  total_tax: string;
  tax_lines: TaxLine[];
  currency_code: string;
  currency_symbol: string;
  currency_minor_unit: number;
  currency_decimal_separator: string;
  currency_thousand_separator: string;
  currency_prefix: string;
  currency_suffix: string;
}

export interface TaxLine {
  name: string;
  price: string;
  rate: string;
}

export interface ShippingRate {
  package_id: number;
  name: string;
  destination: ShippingAddress;
  items: ShippingItem[];
  shipping_rates: ShippingRateMethod[];
}

export interface ShippingItem {
  key: string;
  name: string;
  quantity: number;
}

export interface ShippingRateMethod {
  rate_id: string;
  name: string;
  description: string;
  delivery_time: string;
  price: string;
  taxes: string;
  instance_id: number;
  method_id: ShippingMethodId;
  meta_data: ShippingRateMeta[];
  selected: boolean;
  currency_code: string;
  currency_symbol: string;
  currency_minor_unit: number;
  currency_decimal_separator: string;
  currency_thousand_separator: string;
  currency_prefix: string;
  currency_suffix: string;
}

export interface ShippingRateMeta {
  key: string;
  value: string;
}

export interface CartError {
  code: string;
  message: string;
}
